import React from "react";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Checkbox, IconButton, Stack, TableCell, TableRow } from "@mui/material";
import { PsychologyReverse } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useNavigate, usePrompt } from "hooks";
import { IRecommendationChild, IRecommendationData, RecommendationStatus } from "store/types";

import { useAiDataContext } from "../context/AiData-context";
import { CellWithTooltip } from "./CellWithTooltip";
import { DocumentRead } from "./DocumentRead";

interface AiDataTableRowProps {
  row: IRecommendationData;
  fileId: number;
}

const hasAcceptedParameters = (children?: IRecommendationChild[]): boolean => {
  if (!children?.length) return false;

  return children.some((child) => {
    // check if this is IRecommendationItem with status
    if ("status" in child) {
      if (child.status === RecommendationStatus.accepted) return true;
    }

    // recursively check children
    if ("children" in child && child.children?.length) {
      return hasAcceptedParameters(child.children as IRecommendationChild[]);
    }

    return false;
  });
};

const bodyCellStyle = {
  fontSize: "16px",
  fontWeight: "400",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  position: "relative"
};

export const AiDataTableRow: React.FC<AiDataTableRowProps> = ({ row, fileId }) => {
  const {
    expandedRows,
    toggleRowExpansion,
    regenerateRecommendationData,
    handleHierarchicalCheckboxChange,
    isUniqueIdSelected,
    isUniqueIdPartiallySelected,
    generateUniqueId,
    userHasEditPermission
  } = useAiDataContext();
  const navigate = useNavigate();

  const openPrompt = usePrompt();

  const onOpenFileViewer = async () => {
    const response = await fetch(row.file?.url);
    const blob = await response.blob();
    // eslint-disable-next-line quotes
    const file = new File([blob], row.file?.name || "", { type: blob.type });
    row.file.document_id &&
      (await openPrompt({
        Component: DocumentRead,
        props: {
          file,
          row,
          docId: row.file.document_id
        },
        componentType: "drawer",
        anchor: "right",
        drawerHeader: "PDF Tagger",
        hideBackdrop: true
      }));
  };

  return (
    <TableRow
      sx={{
        borderBottom: expandedRows.has(generateUniqueId(row, fileId)) ? "none !important" : "1px solid #DBE8F5",
        height: "48px",
        "&:hover": {
          backgroundColor: "#F7FAFD"
        },
        "& td": {
          borderBottom: expandedRows.has(generateUniqueId(row, fileId)) ? "none !important" : "1px solid #DBE8F5"
        }
      }}
    >
      <TableCell sx={{ width: "50px", padding: "3px !important" }}>
        <Stack direction="row" alignItems="center" gap="4px">
          {Boolean(row.children?.length) && (
            <IconButton onClick={() => toggleRowExpansion(generateUniqueId(row, fileId))} sx={{ padding: "4px" }}>
              {expandedRows.has(generateUniqueId(row, fileId)) ? (
                <ExpandLessIcon sx={{ fontSize: "20px" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "20px" }} />
              )}
            </IconButton>
          )}
          <Checkbox
            disabled={!userHasEditPermission}
            sx={{
              padding: "4px",
              "&:hover": {
                backgroundColor: "transparent"
              }
            }}
            checked={isUniqueIdSelected(generateUniqueId(row, fileId), row, fileId)}
            indeterminate={isUniqueIdPartiallySelected(row, fileId)}
            onChange={(event) =>
              handleHierarchicalCheckboxChange(generateUniqueId(row, fileId), event.target.checked, row, fileId)
            }
          />
        </Stack>
      </TableCell>
      <CellWithTooltip value={row.file?.name || ""} style={bodyCellStyle} />
      <CellWithTooltip value={row.deal?.title || ""} style={bodyCellStyle} />
      <CellWithTooltip value={dayjs(row.created_at).format("DD/MM/YYYY")} style={bodyCellStyle} />
      <CellWithTooltip value={row.total_count} style={bodyCellStyle} />
      <TableCell sx={{ width: "100px" }}>
        {/* if no file url, file was deleted */}
        <Stack direction="row" alignItems="center" gap={1}>
          {row?.file?.url && row?.file?.document_id && userHasEditPermission && (
            <IconButton onClick={onOpenFileViewer}>
              <MaterialTooltip title={row?.file?.name} isShow>
                <InsertDriveFileOutlinedIcon />
              </MaterialTooltip>
            </IconButton>
          )}
          {row?.file?.url && !hasAcceptedParameters(row.children) && userHasEditPermission && (
            <IconButton
              onClick={() => {
                row.file?.id && regenerateRecommendationData(row.file.id);
              }}
            >
              <MaterialTooltip title={"Regenerate"} isShow>
                <PsychologyReverse sx={{ fill: "#496682" }} />
              </MaterialTooltip>
            </IconButton>
          )}
          {row?.file?.id && row.file.section_id && row?.file?.url && userHasEditPermission && (
            <MaterialTooltip isShow title="PDF Tagger" placement="top">
              <IconButton
                onClick={() => {
                  if (!row.file.section_id) return;
                  navigate({
                    route: "tagger",
                    search: {
                      filesIds: [row.file.id],
                      sections: [row.file.section_id],
                      dealId: row.deal.id,
                      aiData: true
                    },
                    setQueriesMethod: "replace"
                  });
                }}
              >
                <ArticleOutlinedIcon />
              </IconButton>
            </MaterialTooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};
